export interface Profile {
    score: number;
    lastUpdated: number;
    firstVisit: number;
    visitCount: number;
    lastVisit: number;
    activityTime: number; // in seconds
    interests: Record<string, number>;
    tags: string[];
    frequencyAvg: number;
}

export interface ScoringConfig {
    scrollPoints: number;
    timePoints: number;
    timeInterval: number; // in seconds
    mousePoints: number;
    mouseMileageThreshold: number; // in pixels
    touchPoints: number;
    touchTapThreshold: number;
    videoPoints: number;
}

export interface SyncConfig {
    scoreThreshold: number;
    timeThreshold: number; // in seconds
    syncOnExit: boolean;
    onSync?: (profile: Profile) => Promise<Partial<Profile> | void>;
}

export interface URLTrackingConfig {
    enabled: boolean;
    offset: number;
}

export interface SDKConfig extends SyncConfig {
    scoring: ScoringConfig;
    urlTracking: URLTrackingConfig;
    debug?: boolean;
}

export class GtoAnalytics {
    private static STORAGE_KEY = 'gtoAnalytics.profile';
    public profile: Profile;

    private scrollThresholds: Set<number> = new Set();
    private lastTimePoints: number = 0;
    private focusStartTime: number = Date.now();
    private totalFocusTime: number = 0;
    private isFocused: boolean = true;

    private mouseMileage: number = 0;
    private lastMousePos: { x: number, y: number } | null = null;
    private touchCount: number = 0;
    private videoThresholds: Map<HTMLVideoElement, Set<number>> = new Map();

    public config: SDKConfig;
    private lastSyncScore: number = 0;
    private lastSyncTime: number = Date.now();
    private isSyncing: boolean = false;

    constructor() {
        this.profile = this.loadProfile();
        this.config = this.loadConfig();
        this.lastSyncScore = this.profile.score;
        this.lastSyncTime = Date.now();

        this.initializeDOM();
        this.trackVisit();
        this.trackUrlInterests();
        this.trackInterests();
        this.initEventListeners();
        this.startTimeTracking();

        // Initial sync
        this.syncProfile();
    }

    public loadConfig(override?: Partial<SDKConfig>): SDKConfig {
        const defaults: SDKConfig = {
            scoreThreshold: 20,
            timeThreshold: 60,
            syncOnExit: true,
            debug: false,
            urlTracking: {
                enabled: false,
                offset: 0
            },
            scoring: {
                scrollPoints: 2,
                timePoints: 2,
                timeInterval: 10,
                mousePoints: 2,
                mouseMileageThreshold: 100000,
                touchPoints: 2,
                touchTapThreshold: 10,
                videoPoints: 2
            }
        };

        const globalConfig = (window as any).GTO_ANALYTICS_CONFIG || {};
        const config = { ...defaults, ...globalConfig, ...override };

        // Deep merge scoring to avoid losing defaults if only some are provided
        if (globalConfig.scoring || (override && override.scoring)) {
            config.scoring = {
                ...defaults.scoring,
                ...globalConfig.scoring,
                ...(override ? override.scoring : {})
            };
        }

        // Deep merge urlTracking
        if (globalConfig.urlTracking || (override && override.urlTracking)) {
            config.urlTracking = {
                ...defaults.urlTracking,
                ...globalConfig.urlTracking,
                ...(override ? override.urlTracking : {})
            };
        }

        this.config = config;

        if (this.config.debug) {
            console.log('[gtoAnalytics] Configuration loaded:', this.config);
        }

        return this.config;
    }

    /**
     * Toggles debug mode or sets it to a specific state.
     */
    public debug(enabled?: boolean): boolean {
        this.config.debug = enabled !== undefined ? enabled : !this.config.debug;
        console.log(`[gtoAnalytics] Debug mode ${this.config.debug ? 'ENABLED' : 'DISABLED'}`);
        if (this.config.debug) {
            console.log('[gtoAnalytics] Active Config:', this.config);
        }
        return this.config.debug;
    }

    private initEventListeners(): void {
        window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
        document.addEventListener('click', this.handleClick.bind(this));
        window.addEventListener('focus', this.handleFocus.bind(this));
        window.addEventListener('blur', this.handleBlur.bind(this));
        window.addEventListener('mousemove', this.handleMouseMove.bind(this));
        window.addEventListener('touchstart', this.handleTouchStart.bind(this));

        this.initVideoTracking();

        // Use a getter for syncOnExit to allow toggling it after init
        window.addEventListener('beforeunload', () => {
            if (this.config.syncOnExit) {
                this.syncProfile(true);
            }
        });
    }

    private handleMouseMove(e: MouseEvent): void {
        if (!this.lastMousePos) {
            this.lastMousePos = { x: e.clientX, y: e.clientY };
            return;
        }

        const distance = Math.sqrt(
            Math.pow(e.clientX - this.lastMousePos.x, 2) +
            Math.pow(e.clientY - this.lastMousePos.y, 2)
        );

        this.mouseMileage += distance;
        this.lastMousePos = { x: e.clientX, y: e.clientY };

        if (this.mouseMileage >= this.config.scoring.mouseMileageThreshold) {
            this.updateScore(this.config.scoring.mousePoints);
            this.mouseMileage -= this.config.scoring.mouseMileageThreshold;
        }
    }

    private handleTouchStart(): void {
        this.touchCount++;
        if (this.touchCount >= this.config.scoring.touchTapThreshold) {
            this.updateScore(this.config.scoring.touchPoints);
            this.touchCount = 0;
        }
    }

    private initVideoTracking(): void {
        const trackVideos = (container: ParentNode) => {
            container.querySelectorAll('video').forEach(video => {
                video.addEventListener('timeupdate', () => this.handleVideoProgress(video));
            });
        };

        // Track existing videos
        trackVideos(document);

        // Track dynamically added videos
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeName === 'VIDEO') {
                        (node as HTMLVideoElement).addEventListener('timeupdate', () => this.handleVideoProgress(node as HTMLVideoElement));
                    } else if ((node as HTMLElement).querySelectorAll) {
                        trackVideos(node as HTMLElement);
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
    }

    private handleVideoProgress(video: HTMLVideoElement): void {
        if (!video.duration) return;

        const progress = (video.currentTime / video.duration) * 100;
        const thresholds = [25, 50, 75, 100];

        if (!this.videoThresholds.has(video)) {
            this.videoThresholds.set(video, new Set());
        }

        const triggered = this.videoThresholds.get(video)!;
        thresholds.forEach(t => {
            if (progress >= t && !triggered.has(t)) {
                triggered.add(t);
                this.updateScore(this.config.scoring.videoPoints);
            }
        });
    }

    private handleScroll(): void {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;

        if (docHeight <= 0) return;

        const scrollPercent = Math.floor((scrollTop / docHeight) * 100);
        const milestones = [25, 50, 75, 100];

        milestones.forEach(m => {
            if (scrollPercent >= m && !this.scrollThresholds.has(m)) {
                this.scrollThresholds.add(m);
                this.updateScore(this.config.scoring.scrollPoints);
            }
        });
    }

    private handleClick(e: MouseEvent): void {
        const link = (e.target as HTMLElement).closest('a');
        if (link) {
            const weight = link.getAttribute('data-analytics-weight');
            if (weight) {
                const points = parseInt(weight, 10);
                if (!isNaN(points)) {
                    this.updateScore(points);
                }
            }
        }
    }

    private handleFocus(): void {
        this.isFocused = true;
        this.focusStartTime = Date.now();
    }

    private handleBlur(): void {
        if (this.isFocused) {
            this.totalFocusTime += (Date.now() - this.focusStartTime);
            this.isFocused = false;
        }
    }

    private startTimeTracking(): void {
        setInterval(() => {
            if (this.isFocused) {
                const sessionTime = Date.now() - this.focusStartTime;
                const totalSeconds = Math.floor((this.totalFocusTime + sessionTime) / 1000);
                this.profile.activityTime = totalSeconds;

                const interval = this.config.scoring.timeInterval;
                const expectedPoints = Math.floor(totalSeconds / interval) * this.config.scoring.timePoints;

                if (expectedPoints > this.lastTimePoints) {
                    const diff = expectedPoints - this.lastTimePoints;
                    this.updateScore(diff);
                    this.lastTimePoints = expectedPoints;
                }
            }
        }, 1000);
    }

    private loadProfile(): Profile {
        const stored = localStorage.getItem(GtoAnalytics.STORAGE_KEY);
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                console.error('Failed to parse profile', e);
            }
        }

        const now = Date.now();
        return {
            score: 0,
            lastUpdated: now,
            firstVisit: now,
            visitCount: 0,
            lastVisit: now,
            activityTime: 0,
            interests: {},
            tags: [],
            frequencyAvg: 0
        };
    }

    private saveProfile(): void {
        this.profile.lastUpdated = Date.now();
        localStorage.setItem(GtoAnalytics.STORAGE_KEY, JSON.stringify(this.profile));
        this.updateDOM();
    }

    private initializeDOM(): void {
        (window as any).gtoAnalytics = this;
        this.updateDOM();
    }

    private updateDOM(): void {
        // Reserved for future framework integration
    }

    private trackVisit(): void {
        const now = Date.now();
        this.profile.visitCount++;

        if (this.profile.visitCount > 1) {
            const daysSinceFirst = (now - this.profile.firstVisit) / (1000 * 60 * 60 * 24);
            this.profile.frequencyAvg = Math.floor(daysSinceFirst / (this.profile.visitCount - 1));
        }

        this.profile.lastVisit = now;
        this.saveProfile();
    }

    public updateScore(points: number): void {
        this.profile.score += points;
        this.saveProfile();
        this.syncProfile();
    }

    public addInterest(category: string, weight: number = 1): void {
        this.profile.interests[category] = (this.profile.interests[category] || 0) + weight;
        this.saveProfile();
    }

    public addTag(tag: string): void {
        if (!this.profile.tags.includes(tag)) {
            this.profile.tags.push(tag);
            this.saveProfile();
            this.syncProfile(true);
        }
    }

    public removeTag(tag: string): void {
        const index = this.profile.tags.indexOf(tag);
        if (index > -1) {
            this.profile.tags.splice(index, 1);
            this.saveProfile();
            this.syncProfile(true);
        }
    }

    public resetProfile(): void {
        localStorage.removeItem(GtoAnalytics.STORAGE_KEY);
        const now = Date.now();
        this.profile = {
            score: 0,
            lastUpdated: now,
            firstVisit: now,
            visitCount: 1,
            lastVisit: now,
            activityTime: 0,
            interests: {},
            tags: [],
            frequencyAvg: 0
        };
        this.saveProfile();

        // Reset tracking states
        this.scrollThresholds.clear();
        this.lastTimePoints = 0;
        this.focusStartTime = Date.now();
        this.totalFocusTime = 0;
        this.mouseMileage = 0;
        this.touchCount = 0;
        this.videoThresholds.clear();
    }

    public getNormalizedMetrics(): {
        intensity: number;
        loyalty: number;
        tenure: number;
        concentration: Record<string, number>;
    } {
        const { score, activityTime, visitCount, interests, firstVisit } = this.profile;

        const minutes = Math.max(activityTime / 60, 0.1);
        const totalVisits = Math.max(visitCount, 1);
        const totalInterests = Object.values(interests).reduce((sum, val) => sum + val, 0);
        const days = Math.max((Date.now() - firstVisit) / (1000 * 60 * 60 * 24), 0.01);

        return {
            intensity: parseFloat((score / minutes).toFixed(2)),
            loyalty: parseFloat((score / totalVisits).toFixed(2)),
            tenure: parseFloat((score / days).toFixed(2)),
            concentration: Object.entries(interests).reduce((acc, [cat, weight]) => {
                acc[cat] = totalInterests > 0
                    ? parseFloat(((weight / totalInterests) * 100).toFixed(1))
                    : 0;
                return acc;
            }, {} as Record<string, number>)
        };
    }

    /**
     * Extracts categories from the URL path if enabled in config.
     * Sanitizes segments to alphanumeric + hyphen.
     */
    public trackUrlInterests(): void {
        const { enabled, offset } = this.config.urlTracking;
        if (!enabled) return;

        const path = window.location.pathname;
        const segments = path.split('/')
            .filter(s => s.length > 0)
            .slice(offset);

        segments.forEach(segment => {
            // Clean up: alphanumeric and hyphens only
            const category = segment.toLowerCase()
                .replace(/[^a-z0-9-]/g, '-')
                .replace(/-+/g, '-') // collapse multiple hyphens
                .replace(/^-|-$/g, ''); // trim hyphens

            if (category) {
                if (this.config.debug) {
                    console.log(`[gtoAnalytics] Tracking URL category: ${category}`);
                }
                this.addInterest(category, 1);
            }
        });
    }

    public trackInterests(root: Document | HTMLElement = document): void {
        // Meta tags (only on full page tracking)
        if (root === document) {
            document.querySelectorAll('meta[name="keywords"], meta[property="article:tag"], meta[name="gto:categories"]').forEach(meta => {
                const content = meta.getAttribute('content');
                if (content) {
                    content.split(',').map(s => s.trim()).forEach(cat => {
                        if (cat) this.addInterest(cat, 1);
                    });
                }
            });
        }

        // Data attributes
        const selector = '[data-categories]';
        const elements = root instanceof HTMLElement && root.hasAttribute('data-categories')
            ? [root, ...Array.from(root.querySelectorAll(selector))]
            : Array.from(root.querySelectorAll(selector));

        elements.forEach(el => {
            const cats = el.getAttribute('data-categories');
            if (cats) {
                cats.split(',').map(s => s.trim()).forEach(cat => {
                    if (cat) this.addInterest(cat, 1);
                });
            }
        });
    }

    public async syncProfile(force: boolean = false): Promise<void> {
        if (this.isSyncing) {
            return;
        }

        // Check for late-binding config updates if currently using defaults
        if ((window as any).GTO_ANALYTICS_CONFIG && this.config.scoreThreshold === 20 && this.config.timeThreshold === 60) {
            this.loadConfig();
        }

        const now = Date.now();
        const scoreDiff = this.profile.score - this.lastSyncScore;
        const timeDiff = (now - this.lastSyncTime) / 1000;

        if (!force && scoreDiff < this.config.scoreThreshold && timeDiff < this.config.timeThreshold) {
            return; // Thresholds not met
        }

        this.isSyncing = true;

        if (this.config.debug) {
            console.log(`[gtoAnalytics] Starting sync (force=${force}, scoreDiff=${scoreDiff}, timeDiff=${timeDiff.toFixed(1)}s)`);
        }

        try {
            // Custom sync logic
            if (this.config.onSync) {
                // Add a timeout to prevent custom hook from hanging the SDK indefinitely
                const syncTimeout = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Sync timeout')), 10000)
                );

                const result = await Promise.race([
                    this.config.onSync(this.profile),
                    syncTimeout
                ]) as any;

                this.lastSyncScore = this.profile.score;
                this.lastSyncTime = now;

                if (this.config.debug) {
                    console.log('[gtoAnalytics] Custom sync successful', result);
                }

                if (result && typeof result === 'object') {
                    this.profile = { ...this.profile, ...result };
                    this.saveProfile();
                }
                return;
            }

            // Default sync logic (skip on file:// protocol)
            if (window.location.protocol === 'file:') {
                if (this.config.debug) {
                    console.log('[gtoAnalytics] Skipping default sync on file:// protocol');
                }
                return;
            }

            const apiUrl = (window as any).GTO_ANALYTICS_API_URL || '/api/analytics/profile';
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(this.profile),
            });

            if (response.ok) {
                this.lastSyncScore = this.profile.score;
                this.lastSyncTime = now;

                const enrichedProfile = await response.json();
                if (this.config.debug) {
                    console.log('[gtoAnalytics] Default sync successful', enrichedProfile);
                }
                if (enrichedProfile && typeof enrichedProfile === 'object') {
                    this.profile = { ...this.profile, ...enrichedProfile };
                    this.saveProfile();
                }
            } else if (this.config.debug) {
                console.warn('[gtoAnalytics] Default sync failed with status:', response.status);
            }
        } catch (e) {
            console.error('[gtoAnalytics] Failed to sync profile', e);
        } finally {
            this.isSyncing = false;
        }
    }

    public getTopInterests(limit: number = 3): string[] {
        return Object.entries(this.profile.interests)
            .sort(([, a], [, b]) => b - a)
            .slice(0, limit)
            .map(([cat]) => cat);
    }
}
